import {
  MAX_WEBHOOK_CONDITIONS,
  type WebhookEndpoint,
  type WebhookTriggerDraft,
} from '@littlebird/sdk';

import { Icons } from '@/assets/icons/icons';
import CopyButton from '@/components/blocks/CopyButton';
import { XStack, YStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { DropdownMenu } from '@/components/ui/dropdown-menu';

import { WebhookConditionRow } from './webhook-condition-row';

export function WebhookTriggerRow({
  trigger,
  endpoints,
  onChange,
  onRemove,
  onCreateEndpoint,
  onManageEndpoints,
}: {
  trigger: WebhookTriggerDraft;
  endpoints: WebhookEndpoint[];
  onChange: (next: WebhookTriggerDraft) => void;
  onRemove: () => void;
  /** Resolves to the new endpoint's id, or null when the user backs out. */
  onCreateEndpoint: () => Promise<string | null>;
  /** Opens Settings > Webhooks, where secrets, sample payloads, and deletion live. */
  onManageEndpoints: () => void;
}) {
  const selectedEndpoint = endpoints.find(
    (endpoint) => endpoint.id === trigger.webhookEndpointId
  );
  const endpointLabel = selectedEndpoint?.name ?? 'Choose endpoint';
  const fields = selectedEndpoint?.payloadFields ?? [];
  const atConditionLimit = trigger.conditions.length >= MAX_WEBHOOK_CONDITIONS;

  const handleCreateEndpoint = async () => {
    const endpointId = await onCreateEndpoint();
    if (endpointId) onChange({ ...trigger, webhookEndpointId: endpointId });
  };

  return (
    <YStack
      role="group"
      aria-label="Webhook trigger"
      className="gap-2 rounded-xl border border-border bg-surface px-3 py-2"
    >
      <XStack className="flex-wrap items-center gap-2">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-foreground-muted">
          <Icons.plugZap className="size-4 text-background" />
        </span>
        <span className="text-sm text-foreground-muted">Webhook</span>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button
              aria-label={`Webhook endpoint: ${endpointLabel}`}
              variant="outline"
              size="sm"
              className="max-w-52 gap-1.5 px-2"
            >
              <span className="truncate">{endpointLabel}</span>
              <Icons.chevronDown className="size-3.5 shrink-0" />
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="start" className="w-64">
            <DropdownMenu.RadioGroup
              value={trigger.webhookEndpointId ?? ''}
              onValueChange={(value) =>
                onChange({ ...trigger, webhookEndpointId: value })
              }
            >
              {endpoints.map((endpoint) => (
                <DropdownMenu.RadioItem key={endpoint.id} value={endpoint.id}>
                  <YStack className="min-w-0">
                    <span className="truncate">
                      {endpoint.teamId
                        ? `${endpoint.name} (team)`
                        : endpoint.name}
                    </span>
                    {endpoint.description ? (
                      <span className="line-clamp-2 text-xs text-foreground-muted">
                        {endpoint.description}
                      </span>
                    ) : null}
                  </YStack>
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
            {endpoints.length > 0 && <DropdownMenu.Separator />}
            <DropdownMenu.Item onSelect={() => void handleCreateEndpoint()}>
              <Icons.plus className="size-3.5" />
              New endpoint…
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
        <span className="flex-1" />
        <Button
          aria-label="Remove webhook trigger"
          variant="ghost"
          size="icon-sm"
          iconOnly
          className="shrink-0 text-foreground-muted"
          onClick={onRemove}
        >
          <Icons.trash2 />
        </Button>
      </XStack>

      {selectedEndpoint?.description ? (
        <p className="text-xs text-foreground-muted">
          {selectedEndpoint.description}
        </p>
      ) : null}

      {selectedEndpoint && (
        <XStack className="min-w-0 items-center gap-1">
          <span className="min-w-0 flex-1 truncate text-xs text-foreground-muted">
            {selectedEndpoint.url}
          </span>
          <CopyButton
            text={selectedEndpoint.url}
            copyText="Copy webhook URL"
            size="sm"
          />
        </XStack>
      )}

      {trigger.conditions.map((condition, index) => (
        <WebhookConditionRow
          key={index}
          condition={condition}
          fields={fields}
          onChange={(next) =>
            onChange({
              ...trigger,
              conditions: trigger.conditions.map((existing, i) =>
                i === index ? next : existing
              ),
            })
          }
          onRemove={() =>
            onChange({
              ...trigger,
              conditions: trigger.conditions.filter((_, i) => i !== index),
            })
          }
        />
      ))}

      <Button
        variant="outline"
        size="sm"
        className="h-8 w-full border-dashed px-3 shadow-none hover:enabled:bg-surface-hover [&>span]:w-full [&>span]:justify-start"
        disabled={atConditionLimit}
        onClick={() =>
          onChange({
            ...trigger,
            conditions: [
              ...trigger.conditions,
              { path: '', operator: 'equals', value: '', negate: false },
            ],
          })
        }
      >
        <Icons.plus className="size-3.5" />
        {atConditionLimit
          ? `Up to ${MAX_WEBHOOK_CONDITIONS} conditions`
          : 'Add condition'}
      </Button>

      <Button
        variant="link"
        size="sm"
        className="w-fit self-end text-foreground-muted"
        onClick={onManageEndpoints}
      >
        Manage endpoints
      </Button>
    </YStack>
  );
}
